<template>
  <div v-if="hasSelectedWorkpackage" class="architecture-v2-container">
    <div class="filter-bar-wrapper">
      <div class="filter-bar">
        <div class="filter-item">
          <span class="filter-label">{{ $t('filters.pricingGroup') }}:</span>
          <pricing-filter
            :filter="pricingGroupFilter"
            :loading="hierarchyLoading"
            class="filter-dropdown"
            hide-label
            @filterSelected="onSelectFilter"
          />
        </div>

        <div class="filter-item">
          <span class="filter-label">{{ $t('filters.scenario') }}:</span>
          <pricing-filter
            :filter="scenarioFilter"
            :loading="scenarioLoading"
            :disabled="scenarioCalculating"
            class="filter-dropdown"
            hide-label
            @filterSelected="onSelectFilter"
          />
        </div>

        <div class="filter-item">
          <span class="filter-label">{{ $t('filters.architectureGroup') }}:</span>
          <pricing-filter
            :filter="architectureGroupFilter"
            :loading="hierarchyLoading"
            class="filter-dropdown"
            hide-label
            @filterSelected="onSelectFilter"
          />
        </div>
      </div>
    </div>

    <div class="tabs-section">
      <tabs :tabs="tabs" />
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { isEmpty, sortBy, toLower } from 'lodash';
import { ascending } from '@enums/sort-direction';
import { pricingGroupLevel, architectureGroupLevel } from '@enums/hierarchy';
import hierarchyFiltersMixin from '../../../mixins/hierarchyFilters';
import Tabs from '../../../components/tabs.vue';
import ArchitectureTab from './tabs/architecture-tab.vue';

export default {
  name: 'ArchitectureV2',

  components: {
    Tabs,
    // eslint-disable-next-line vue/no-unused-components
    ArchitectureTab,
  },
  mixins: [hierarchyFiltersMixin],

  data() {
    return {
      pricingGroups: [],
      architectureGroups: [],
    };
  },

  computed: {
    ...mapGetters('workpackages', ['hasSelectedWorkpackage']),
    ...mapState('filters', ['scenario', 'architectureGroup', 'pricingGroup']),
    ...mapGetters('context', ['isPricingSpecialist']),
    ...mapState('scenarioMetadata', {
      scenarioMetadata: state => state.scenarioMetadata,
      scenarioLoading: state => state.loading,
      scenarioCalculating: state => !isEmpty(state.calculating),
    }),
    ...mapState('hierarchy', {
      hierarchyLoading: state => state.loading,
    }),
    ...mapGetters('hierarchy', ['getHierarchy']),

    isRulesEnabled() {
      return !!this.pricingGroup && !!this.scenario && !!this.architectureGroup;
    },

    canEditRules() {
      return this.isPricingSpecialist;
    },

    pricingGroupFilter() {
      return {
        name: this.$t('filters.pricingGroup'),
        state: 'pricingGroup',
        values: sortBy(this.pricingGroups, pg => toLower(pg.levelEntryDescription)),
        label: 'levelEntryDescription',
        itemValue: 'levelEntryKey',
      };
    },

    scenarioFilter() {
      const sortedScenarios = sortBy(this.scenarioMetadata, s => toLower(s.scenarioDescription));
      return {
        name: this.$t('filters.scenario'),
        itemValue: 'scenarioKey',
        state: 'scenario',
        values: sortedScenarios,
        label: 'scenarioDescription',
      };
    },

    architectureGroupFilter() {
      return {
        name: this.$t('filters.architectureGroup'),
        state: 'architectureGroup',
        values: sortBy(this.architectureGroups, ag => toLower(ag.levelEntryDescription)),
        label: 'levelEntryDescription',
        itemValue: 'levelEntryKey',
      };
    },

    tabs() {
      return [
        {
          label: this.$t('tabText.architectureRules'),
          disabled: false,
          component: ArchitectureTab,
          props: { tabType: 'rules' },
          routeNames: ['architecture-rules'],
          navigateTo: () =>
            this.$router.push({
              name: 'architecture-rules',
              params: { workpackageId: this.$route.params.workpackageId },
            }),
        },
        {
          label: this.$t('tabText.linePricingRules'),
          disabled: false,
          component: ArchitectureTab,
          props: { tabType: 'pricing' },
          routeNames: ['line-pricing-rules'],
          navigateTo: () =>
            this.$router.push({
              name: 'line-pricing-rules',
              params: { workpackageId: this.$route.params.workpackageId },
            }),
        },
      ];
    },
  },

  watch: {
    async isRulesEnabled(newValue, oldValue) {
      if (newValue && !oldValue) {
        await this.loadRulesData();
      }
    },

    async architectureGroup(newValue, oldValue) {
      if (newValue && newValue !== oldValue && this.pricingGroup && this.scenario) {
        await this.loadRulesData();
      }
    },
  },

  async created() {
    await this.clearStaleData();
    await this.initializeData();
  },

  methods: {
    ...mapActions('hierarchy', ['fetchHierarchy', 'hierarchiesWithProducts']),
    ...mapActions('scenarioMetadata', ['fetchScenarioMetadata']),
    ...mapMutations('scenarioMetadata', ['setScenarioMetadata']),
    ...mapActions('architectureDrivers', ['fetchArchitectureDrivers']),
    ...mapActions('filters', ['setSelectedFilter']),
    ...mapMutations('notifications', ['addNotification']),

    async clearStaleData() {
      // Clear scenario metadata if it doesn't match the current pricing group
      if (this.pricingGroup && !isEmpty(this.scenarioMetadata)) {
        const firstScenario = this.scenarioMetadata[0];
        if (firstScenario && firstScenario.hierarchyId !== this.pricingGroup) {
          this.setScenarioMetadata([]);
        }
      }
    },

    async initializeData() {
      // Load pricing groups if not already loaded
      if (isEmpty(this.getHierarchy({ level: pricingGroupLevel }))) {
        await this.fetchHierarchy({
          params: {
            where: { level: pricingGroupLevel },
            sortBy: 'levelEntryDescription',
            sortDirection: ascending,
          },
        });
      }
      this.pricingGroups = this.getHierarchy({ level: pricingGroupLevel });

      // If we have a pricing group (restored from localStorage), load dependent data
      if (this.pricingGroup) {
        if (isEmpty(this.scenarioMetadata)) {
          await this.fetchScenarioMetadata({
            params: {
              where: { hierarchyId: this.pricingGroup },
              sortBy: 'scenarioDescription',
              sortDirection: ascending,
            },
            unsetFilter: false,
          });
        }

        await this.getArchitectureGroups(this.pricingGroup);

        // Auto-populate filters if only one option exists and not already selected
        this.autoPopulateFilters(true);
      }

      // Load rules data if all filters are selected
      if (this.isRulesEnabled) {
        await this.loadRulesData();
      }
    },

    async loadRulesData() {
      try {
        await Promise.all([this.fetchArchitectureDrivers()]);
      } catch (error) {
        this.addNotification({
          id: `architecture-rules-load-error-${Date.now()}`,
          jobStatus: 'failed',
          baseTranslation: 'notifications.architectureRulesLoad',
        });
      }
    },

    autoPopulateFilters(skipAlreadySelected = false) {
      // Auto-populate Scenario if only one option exists
      if (this.scenarioMetadata && this.scenarioMetadata.length === 1) {
        if (!skipAlreadySelected || !this.scenario) {
          const singleScenario = this.scenarioMetadata[0];
          this.setSelectedFilter({
            filterName: 'scenario',
            filterValue: singleScenario.scenarioKey,
          });
        }
      }

      // Auto-populate Architecture Group if only one option exists
      if (this.architectureGroups && this.architectureGroups.length === 1) {
        if (!skipAlreadySelected || !this.architectureGroup) {
          const singleArchitectureGroup = this.architectureGroups[0];
          this.setSelectedFilter({
            filterName: 'architectureGroup',
            filterValue: singleArchitectureGroup.levelEntryKey,
          });
        }
      }
    },

    async onSelectFilter({ filterName, filterValue }) {
      this.setSelectedFilter({ filterName, filterValue });

      if (filterName === 'pricingGroup' && filterValue) {
        // Load scenarios and architecture groups for this pricing group
        await this.fetchScenarioMetadata({
          params: {
            where: { hierarchyId: filterValue },
            sortBy: 'scenarioDescription',
            sortDirection: ascending,
          },
          unsetFilter: false,
        });

        await this.getArchitectureGroups(filterValue);

        // Auto-populate filters if only one option exists
        this.autoPopulateFilters();
      }
    },

    async getArchitectureGroups(pricingGroupFilter) {
      await this.fetchHierarchy({
        params: {
          sortBy: 'levelEntryDescription',
          sortDirection: ascending,
        },
      });
      this.architectureGroups = await this.hierarchiesWithProducts({
        level: architectureGroupLevel,
        parentId: pricingGroupFilter,
      });
    },

    ...mapActions('hierarchy', ['fetchHierarchy', 'hierarchiesWithProducts']),
    ...mapActions('scenarioMetadata', ['fetchScenarioMetadata']),
    ...mapActions('architectureGroup', ['fetchLinePricingGroupsWithProducts']),
    ...mapActions('architectureDrivers', ['fetchArchitectureDrivers']),
  },
};
</script>

<style lang="scss" scoped>
// Main container
.architecture-v2-container {
  background-color: white;
  margin: 0;
  padding: 0;
  position: relative;

  &:first-child {
    margin-top: 0 !important;
    padding-top: 0 !important;
    padding-left: 10px;
  }
}

// Filter bar wrapper - breaks out of parent container constraints
.filter-bar-wrapper {
  position: relative;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  margin-top: -16px; // Pull up to eliminate any parent spacing
  margin-bottom: 0;
  width: 100vw;
  background-color: #e8eaf6;
  border-bottom: 1px solid #e0e0e0;
}

// Filter bar - exact match to target design
.filter-bar {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  padding-left: 36px;
  gap: 32px; // Space between filter items
  max-width: 100%; // Reasonable max width
  margin: 0 auto; // Center the content
}

// Each filter item (label + dropdown pair)
.filter-item {
  display: flex;
  align-items: center;
  gap: 8px; // Space between label and dropdown

  .filter-label {
    font-size: 16px;
    font-weight: 500;
    color: rgba(0, 0, 0, 0.87);
    white-space: nowrap;
    margin-right: 4px;
  }

  .filter-dropdown {
    min-width: 180px;

    // Remove spacing from pricing-filter component
    ::v-deep .v-layout {
      margin: 0;
      padding: 0;

      .page-rule-filter {
        display: none !important; // Hide any remaining internal labels
      }
    }

    // Style the dropdown to match target
    ::v-deep .v-select {
      .v-input__control {
        min-height: 36px;

        .v-input__slot {
          background-color: white !important;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          box-shadow: none;
          padding: 0 12px;
          min-height: 36px;
          margin: 0;

          &:hover {
            border-color: #9ca3af;
          }

          .v-select__selection {
            font-size: 14px;
            color: rgba(0, 0, 0, 0.87);
            line-height: 20px;
          }

          .v-input__append-inner {
            margin-top: 8px;

            .v-icon {
              color: rgba(0, 0, 0, 0.54);
              font-size: 20px;
            }
          }
        }
      }

      &.v-input--is-focused {
        .v-input__slot {
          border-color: #1976d2 !important;
          border-width: 2px;
        }
      }

      // Hide helper text area
      .v-text-field__details {
        display: none !important;
      }
    }

    // Remove any padding from the autocomplete
    ::v-deep .v-autocomplete {
      padding: 0 !important;
      margin: 0 !important;
    }
  }
}

.tabs-section {
  margin-top: 5px;
}

// Keep only the no-filters-message styling
.no-filters-message {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  text-align: center;
  color: rgba(0, 0, 0, 0.6);
  font-size: 16px;
}
</style>
